/**
 * 行业技能系统（D-57，蓝图 P2 后段"skill 行业技能"）。
 *
 * 两块：
 *  ① SKILL_TEMPLATES——题材技能模板：选题材一键生成"预置画布"（落下文学剧本节点、预填该题材专业提示词），
 *     首页"技能/模板库"消费；
 *  ② NODE_SKILL_SNIPPETS——环节技能片段：把该环节的专业写法（三幕/爽点/钩子/8 层…）一键追加到提示词。
 * 起步为纯前端内置表（mock），接真 LLM/后端后可由服务器下发。全部纯数据 + 纯函数，可测。
 */
import { createEmptyCanvas, type CanvasDoc } from './canvas';
import { createTextGenNode, type TextGenPayload } from './generation';

export interface SkillTemplate {
  id: string;
  name: string;
  genre: string;
  blurb: string;
  /** 预填到文学剧本节点的创作提示词 */
  scriptPrompt: string;
  /** 推荐质感风格 id（对应 model/styles） */
  styleId?: string | undefined;
}

export const SKILL_TEMPLATES: SkillTemplate[] = [
  {
    id: 'suspense', name: '悬疑反转', genre: '悬疑', blurb: '冷开场抛钩子 · 每 90 秒一反转', styleId: 'real',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，悬疑反转题材。要求：冷开场即抛出尸体/失踪/秘密强钩子；主角迅速陷入困境；每 90 秒一个反转、结尾留最强钩子；高密度对白推动剧情、少旁白；场景不超过 5 个。输出：剧名、logline、分场剧本（场景/人物/动作/对白）。',
  },
  {
    id: 'romance', name: '都市甜宠', genre: '甜宠', blurb: '高糖节奏 · 双向奔赴', styleId: 'real',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，都市甜宠题材。要求：开场即制造男女主强反差相遇的名场面；三分钟内一个甜点、五分钟一个小高潮；对白撩人、节奏明快；结尾留误会或身份反转钩子。输出剧名、logline、分场剧本。',
  },
  {
    id: 'costume', name: '古风权谋', genre: '古风', blurb: '考究服化道 · 朝堂暗战', styleId: 'guofeng-pro',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，古风权谋题材。要求：以一场算计/背叛开场立住主角处境；台词古雅凝练、机锋暗藏；服化道与场景体现朝代质感；每场埋一条权谋伏笔，结尾留生死局钩子。输出剧名、logline、分场剧本。',
  },
  {
    id: 'rebirth', name: '穿越重生', genre: '穿越', blurb: '金手指开局 · 逆袭爽感', styleId: 'real',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，穿越重生题材。要求：开场即"重生/穿越"金手指设定；主角用先知优势快速打脸逆袭；爽点密集、节奏快；对白口语有梗；结尾立一个新目标或强敌钩子。输出剧名、logline、分场剧本。',
  },
  {
    id: 'scifi', name: '科幻末世', genre: '科幻', blurb: '世界观钩子 · 生存张力', styleId: 'scifi',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，科幻末世题材。要求：用一个视觉化的末世奇观开场；快速交代规则与威胁；主角面临两难生存抉择；对白简洁有张力、信息靠动作透露；结尾抛出更大的世界观钩子。输出剧名、logline、分场剧本。',
  },
  {
    id: 'comedy', name: '喜剧沙雕', genre: '喜剧', blurb: '密集笑点 · 反差梗', styleId: 'anime-jp',
    scriptPrompt:
      '写一部竖屏短剧第 1 集文学剧本，喜剧沙雕题材。要求：开场即荒诞情境；每 15-20 秒一个笑点或反差梗；对白口语、节奏快、可玩梗；人物动机简单但行为离谱；结尾一个更大的乌龙钩子。输出剧名、logline、分场剧本。',
  },
];

export interface SkillSnippet {
  id: string;
  label: string;
  /** 追加到提示词的专业片段 */
  text: string;
}

/** 文学剧本环节的技能片段（一键追加到提示词）。 */
export const SCRIPT_SKILLS: SkillSnippet[] = [
  { id: 'three-act', label: '三幕结构', text: '按三幕结构：建置（钩子+困境）→ 对抗（升级与反转）→ 结局（留强钩子）。' },
  { id: 'beat', label: '爽点节奏', text: '每 90 秒一个爽点或反转，前 3 秒必须有强钩子。' },
  { id: 'dialogue', label: '台词密度', text: '对白驱动、高密度台词，少旁白，用台词塑造人物与推进冲突。' },
  { id: 'hook', label: '结尾钩子', text: '结尾必须留下未解悬念或反转，驱动观众看下一集。' },
];

/** 由题材技能生成一张**预置连线**的起手画布：文学剧本（预填提示词）→ 导演剧本，进画布即可往下拆分镜。 */
export function buildSkillCanvas(skill: SkillTemplate, canvasId: string, nowIso: string): CanvasDoc {
  const doc = createEmptyCanvas(canvasId, nowIso);
  const script = createTextGenNode('textToScript', `${canvasId}-skill-1`, { x: 120, y: 140 });
  const scriptPayload: TextGenPayload = { ...(script.payload as TextGenPayload), prompt: skill.scriptPrompt };
  const scriptNode: typeof script = { ...script, payload: scriptPayload, title: `${skill.name} · 文学剧本` };
  const director = createTextGenNode('scriptToStoryboard', `${canvasId}-skill-2`, { x: 480, y: 140 });
  const edge = { id: `${canvasId}-skill-e1`, from: scriptNode.id, to: director.id };
  return { ...doc, title: `${skill.name}（${skill.genre}）`, nodes: [scriptNode, director], edges: [edge] };
}

/** 把技能片段追加到提示词（去重、空则直接用）。 */
export function applySkillSnippet(prompt: string, snippet: SkillSnippet): string {
  const p = prompt.trim();
  if (p === '') {
    return snippet.text;
  }
  if (p.includes(snippet.text)) {
    return p;
  }
  return `${p}\n${snippet.text}`;
}
